import { ApolloClient, createHttpLink, InMemoryCache, ApolloLink, ApolloError, from } from "@apollo/client/core";
import { onError } from "@apollo/client/link/error";
import { useAccessGroupStore } from "~/composables/stores/accessGroup";
import { useAuth0 } from "~/composables/useAuth0";
import { logErrorMessages } from "@vue/apollo-util";
import { DefaultApolloClient, provideApolloClient } from "@vue/apollo-composable";

export default defineNuxtPlugin({
  name: "apollo",
  dependsOn: ["auth0"],
  async setup(app) {
    const AUTH0_TOKEN = "auth0-token";
    const AUTH_ERROR = "AUTH_ERROR";
    const PERMISSION_ERROR_MESSAGE = "Missing permissions";

    const accessGroupStore = useAccessGroupStore();
    const runtimeConfig = useRuntimeConfig()

    // HTTP connection to the API
    const httpLink = createHttpLink({
      uri: runtimeConfig.public.gateway
    });

    // Middleware to add the authentication token to every request
    const authMiddleware = new ApolloLink((operation, forward) => {
      const headers = {
        ...operation.getContext().headers,
        authorization: `Bearer ${getAuth0Token()}`,
      };

      const accessGroup = accessGroupStore.accessGroup;

      if (accessGroup) {
        headers["Access-Group-Context"] = accessGroup;
      }

      operation.setContext({
        headers,
      });

      return forward(operation);
    });

    // Middleware to add error handling
    const errorMiddleware = onError(({ networkError, graphQLErrors }) => {
      if (networkError && app.$config.public.buildVariant !== "prod") {
        // If not in production we can log the error
        logErrorMessages(networkError as ApolloError);
      }

      // If the error code is 401, redirect to login
      if (networkError && "statusCode" in networkError && networkError.statusCode === 401) {
        if (graphQLErrors && graphQLErrors.some((error) => error.extensions?.code == AUTH_ERROR && error.message === PERMISSION_ERROR_MESSAGE)) {
          // If the error is a permission error, we should not redirect to login
          return;
        }

        const { $router } = useNuxtApp();

        useAuth0().loginWithRedirect({
          appState: {
            targetUrl: $router.currentRoute.value.path || "/",
          },
        });

        return;
      }
    });

    // Options
    const defaultOptions = {
      query: {
        fetchPolicy: "cache-first",
        errorPolicy: "ignore",
      },
    };

    // Cache
    const cache = new InMemoryCache({});

    function getAuth0Token() {
      return localStorage.getItem(AUTH0_TOKEN);
    }

    // Create the apollo client
    const apolloClient = new ApolloClient({
      link: from([authMiddleware, errorMiddleware, httpLink]),
      cache: cache,
      queryDeduplication: true,
      defaultOptions: {
        query: {
          fetchPolicy: "cache-first",
          errorPolicy: "ignore",
        },
      },
    });

    provideApolloClient(apolloClient);

    app.vueApp.provide(DefaultApolloClient, apolloClient);
  },
});
